/**
 * @fileoverview Header Controller Module.
 * Manages the behavior and interactions of the header element, including the mega menu, scroll behavior, and action bar stickiness.
 */

import {elementExists} from "../Utils";

/**
 * Controller for managing header interactions and behaviors.
 */
const HeaderController = {
    HeaderSelector: '#main-navbar',
    MegaMenu: "#mega-container",
    BottomMenu: "#mega-bottom-menu",
    InlineMenu: "#inline-menu",
    Highlight: '.menu-highlight',
    ActionSticky: '#action-content-bar',
    TeamSticky: '#team-bar',
    TagSticky: '#tag-bar',
    MobileTeamSticky: '#team-bar-mobile',
    MobileTagSticky: '#tag-bar-mobile',
    ReturnTop: "#return-top",

    /**
     * Initializes the HeaderController by setting up listeners and handling specific scroll behaviors.
     */
    init() {
        if (elementExists(this.HeaderSelector) && elementExists(this.MegaMenu) ) {
            const container = document.querySelector(this.HeaderSelector);
            const megaMenu = document.querySelector(this.MegaMenu);
            this.setListeners(container, megaMenu );

            const bottomMenu = document.querySelector(this.BottomMenu);
            this.setLastSubMenuChild(bottomMenu.lastChild.previousSibling)

            if (elementExists(this.ActionSticky)) { 
                const actionSticky = document.querySelector(this.ActionSticky);
                this.handleActionScroll(container, actionSticky);
            }

            if (elementExists(this.ReturnTop)) {
                const returnTop = document.querySelector(this.ReturnTop);
                this.returnTopPage(returnTop);
                this.handleGeneralScroll(container, returnTop);
            }

            if (elementExists(this.TeamSticky)) { 
                this.setAnchors();
                const teamSticky = document.querySelector(this.TeamSticky);
                this.handleTeamScroll(container, teamSticky)
            }

            if (elementExists(this.TagSticky)) { 
                this.setAnchors();
                const tagSticky = document.querySelector(this.TagSticky);
                this.handleTagScroll(container, tagSticky)
            }

            if (elementExists(this.MobileTagSticky)) { 
                this.setAnchors();
                const mobileTagSticky = document.querySelector(this.MobileTagSticky);
                this.handleTagMobileScroll(container, mobileTagSticky)
            }

            if (elementExists(this.MobileTeamSticky)) { 
                this.setAnchors();
                const mobileTeamSticky = document.querySelector(this.MobileTeamSticky);
                this.handleTeamMobileScroll(container, mobileTeamSticky)
            }
        }
    },

    /**
     * Sets up various event listeners for the header.
     * @param {HTMLElement} child - The last child element of the bottom menu.
     */
    setLastSubMenuChild(child) {
        child.innerHTML = `
            <a href=\"/become-a-member/\">
            <div class="flex items-center px-2 py-3 btn-primary btn md:text-body-regular hover:scale-100">
            <button class="flex items-center justify-between w-full gap-4">
                Become a member
                <div class="relative hidden lg:flex items-center justify-center overflow-hidden">
                    <svg class="btn-arrow" width="20" viewBox="0 0 28 24" fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12H25" stroke="currentColor" stroke-width="2" stroke-linecap="square"
                            stroke-linejoin="round"></path>
                        <path d="M19.434 5L26.434 12L19.434 19" stroke="currentColor" stroke-width="2"
                            stroke-linecap="square"></path>
                    </svg>
                </div>
            </button>
        </div>
         </a>`;
    },

    /**
     * Sets up various event listeners for the header.
     * @param {HTMLElement} container - The main header container element.
     * @param {HTMLElement} megaMenu - The mega menu element.
     */
    setListeners(container, megaMenu ) {
        window.addEventListener('scroll', () => this.handleScroll(container));
        document.querySelector('.burger').addEventListener('click', () => this.openMegaMenu(megaMenu));
        document.querySelector('#close-megamenu').addEventListener('click', () => this.closeMegaMenu(megaMenu));
        document.addEventListener('click', (event) => this.handleClickOutsideMegaMenu(event, megaMenu));
    },

    /**
     * Handles click events outside the mega menu to close the menu.
     * @param {Event} event - The event object.
     * @param {HTMLElement} megaMenu - The mega menu element.
     */
    handleClickOutsideMegaMenu(event, megaMenu) {
        if ( !document.querySelector('#mega-menu').contains(event.target) 
            && document.querySelector('#mega-container').contains(event.target) )
            this.closeMegaMenu(megaMenu)
    },

    /**
     * Handles the scroll event to toggle the sticky class on the header.
     * @param {HTMLElement} container - The main header container element.
     */

    handleScroll(container) {
        if (window.scrollY > 0) {
            container.classList.add('stick');
        } else {
            container.classList.remove('stick');
        }
    },

    /**
     * Opens the mega menu and disables body scroll.
     * @param {HTMLElement} megaMenu - The mega menu element.
     */
    openMegaMenu(megaMenu) {
        document.querySelector('body').style.overflow = 'hidden';
        megaMenu.classList.add('open');
    },

   /**
    * Opens the mega menu and disables body scroll.
    * @param {HTMLElement} megaMenu - The mega menu element.
    */
    returnTopPage(button) {
        button.addEventListener('click', () => {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
    },


    setAnchors() {
        document.querySelectorAll('.team-anchor-js').forEach(anchor => {
            anchor.addEventListener('click', e => {
                e.preventDefault();
                const targetId = anchor.getAttribute('href');
                const targetElement = document.querySelector(targetId);
                const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - 100;

                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth'
                });
            });
        });
        document.querySelectorAll('.tag-anchor-js').forEach(anchor => {
            anchor.addEventListener('click', e => {
                e.preventDefault();
                const targetId = anchor.getAttribute('href');
                const targetElement = document.querySelector(targetId);
                const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - 175;

                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth'
                });
            });
        });
        document.querySelectorAll('.tag-anchor-mobile-js').forEach(anchor => {
            anchor.addEventListener('click', e => {
                e.preventDefault();
                const targetId = anchor.getAttribute('href');
                const targetElement = document.querySelector(targetId);
                let targetPosition = 0;
                if (window.pageYOffset < 110) {
                    targetPosition = targetElement.getBoundingClientRect().top - window.innerHeight - 100;
                } else {
                    targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - 100;
                }

                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth'
                });
            });
        });
    },

    /**
     * Closes the mega menu and enables body scroll.
     * @param {HTMLElement} megaMenu - The mega menu element.
     */
    closeMegaMenu(megaMenu) {
        document.querySelector('body').style.overflow = 'auto';
        megaMenu.classList.remove('open');
    },

    /**
     * Handles the scroll behavior for the action sticky bar.
     * @param {HTMLElement} container - The main header container element.
     * @param {HTMLElement} teamSticky - The action content bar element.
     */
    handleTeamScroll(container, teamSticky) {
        const teamStickyPosition = teamSticky.offsetTop;
        const containerUnstickPosition = teamStickyPosition - container.offsetHeight;

        const onScroll = () => {
            const scrollY = window.scrollY;
        
            if (scrollY > teamStickyPosition) {
            container.classList.add('-translate-y-full');
            teamSticky.classList.add('fixed', 'top-0', 'z-10', 'left-0', 'bg-secondary-olive');
            teamSticky.classList.remove('container');
            } else {
                teamSticky.classList.remove('fixed', 'top-0', 'z-10', 'left-0', 'bg-secondary-olive');
                teamSticky.classList.add('container');
            }
        
            if (scrollY > containerUnstickPosition) {
            container.classList.add('-translate-y-full');
            } else {
            container.classList.remove('-translate-y-full');
            }
        };

        const debounce = (func, delay) => {
            let inDebounce;
            return function() {
              const context = this;
              const args = arguments;
              clearTimeout(inDebounce);
              inDebounce = setTimeout(() => func.apply(context, args), delay);
            };
        };
        
          window.addEventListener('scroll', debounce(onScroll, 10));
    },

    /**
     * Handles the scroll behavior for the action sticky bar.
     * @param {HTMLElement} container - The main header container element.
     * @param {HTMLElement} tagSticky - The action content bar element.
     */
    handleTagScroll(container, tagSticky) {
        const tagStickyPosition = tagSticky.offsetTop;
        const containerUnstickPosition = tagStickyPosition - container.offsetHeight;

        const onScroll = () => {
            const scrollY = window.scrollY;
        
            if (scrollY > tagStickyPosition) {
                container.classList.add('-translate-y-full');
                tagSticky.classList.add('fixed', 'top-0', 'z-10', 'left-0', 'bg-secondary-olive');
            } else {
                tagSticky.classList.remove('fixed', 'top-0', 'z-10', 'left-0', 'bg-secondary-olive');
            }
            if (scrollY > containerUnstickPosition) {
                container.classList.add('-translate-y-full');
            } else {
                container.classList.remove('-translate-y-full');
            }
        };

        const debounce = (func, delay) => {
            let inDebounce;
            return function() {
              const context = this;
              const args = arguments;
              clearTimeout(inDebounce);
              inDebounce = setTimeout(() => func.apply(context, args), delay);
            };
        };
        
          window.addEventListener('scroll', debounce(onScroll, 10));
    },

    /**
     * Handles the scroll behavior for the action sticky bar.
     * @param {HTMLElement} container - The main header container element.
     * @param {HTMLElement} mobileTeamSticky - The action content bar element.
     */
    handleTeamMobileScroll(container, mobileTeamSticky) {
        const mobileTeamStickyPosition = mobileTeamSticky.offsetTop;
        const containerUnstickPosition = mobileTeamStickyPosition - container.offsetHeight;

        const onScroll = () => {
            const scrollY = window.scrollY;
        
            if (scrollY > mobileTeamStickyPosition) {
            container.classList.add('-translate-y-full');
            mobileTeamSticky.classList.add('fixed', 'top-0', 'z-10', 'w-full', 'inset-x-0', 'bg-secondary-olive', 'py-2');
            mobileTeamSticky.classList.remove('pt-4');
        } else {
            mobileTeamSticky.classList.remove('fixed', 'top-0', 'z-10', 'w-full', 'inset-x-0', 'bg-secondary-olive', 'py-2');
            mobileTeamSticky.classList.add('pt-4');
            }
        
            if (scrollY > containerUnstickPosition) {
            container.classList.add('-translate-y-full');
            } else {
            container.classList.remove('-translate-y-full');
            }
        };

        const debounce = (func, delay) => {
            let inDebounce;
            return function() {
              const context = this;
              const args = arguments;
              clearTimeout(inDebounce);
              inDebounce = setTimeout(() => func.apply(context, args), delay);
            };
          };
        
          window.addEventListener('scroll', debounce(onScroll, 10));
    },

    /**
     * Handles the scroll behavior for the action sticky bar.
     * @param {HTMLElement} container - The main header container element.
     * @param {HTMLElement} mobileTeamSticky - The action content bar element.
     */
    handleTagMobileScroll(container, mobileTagSticky) {
        const mobileTagStickyPosition = mobileTagSticky.offsetTop;
        const containerUnstickPosition = mobileTagStickyPosition - container.offsetHeight;

        const onScroll = () => {
            const scrollY = window.scrollY;
        
            if (scrollY > mobileTagStickyPosition) {
            container.classList.add('-translate-y-full');
            mobileTagSticky.classList.add('fixed', 'top-0', 'z-10', 'w-full', 'inset-x-0', 'container', 'bg-secondary-olive', 'py-2');
            mobileTagSticky.classList.remove('pt-4');
        } else {
            mobileTagSticky.classList.remove('fixed', 'top-0', 'z-10', 'w-full', 'inset-x-0', 'container', 'bg-secondary-olive', 'py-2');
            mobileTagSticky.classList.add('pt-4');
            }
        
            if (scrollY > containerUnstickPosition) {
            container.classList.add('-translate-y-full');
            } else {
            container.classList.remove('-translate-y-full');
            }
        };

        const debounce = (func, delay) => {
            let inDebounce;
            return function() {
              const context = this;
              const args = arguments;
              clearTimeout(inDebounce);
              inDebounce = setTimeout(() => func.apply(context, args), delay);
            };
          };
        
          window.addEventListener('scroll', debounce(onScroll, 10));
    },

    /**
     * Handles the scroll behavior for the action sticky bar.
     * @param {HTMLElement} container - The main header container element.
     * @param {HTMLElement} actionSticky - The action content bar element.
     */
    handleActionScroll(container, actionSticky) {
        const actionStickyPosition = actionSticky.offsetTop;
        const containerUnstickPosition = actionStickyPosition - container.offsetHeight;

        const onScroll = () => {
            const scrollY = window.scrollY;
            if (scrollY > actionStickyPosition) {
                container.classList.add('-translate-y-full');
                actionSticky.classList.add('fixed', 'top-0', 'z-10', 'bg-secondary-olive');
            } else {
                actionSticky.classList.remove('fixed', 'top-0', 'z-10', 'bg-secondary-olive');
            }
            
            if (scrollY > containerUnstickPosition) {
                container.classList.add('-translate-y-full');
            } else {
                container.classList.remove('-translate-y-full');
            }
        };

        const debounce = (func, delay) => {
            let inDebounce;
            return function() {
              const context = this;
              const args = arguments;
              clearTimeout(inDebounce);
              inDebounce = setTimeout(() => func.apply(context, args), delay);
            };
          };
        
          window.addEventListener('scroll', debounce(onScroll, 10));
    },

    handleGeneralScroll(container, returnTopAction) {
        console.log('Entered')
        const containerUnstickPosition = 500 - container.offsetHeight;
        const onScroll = () => {
            const scrollY = window.scrollY;
            if (scrollY > 490) {
                container.classList.add('-translate-y-full');
                returnTopAction.classList.remove('hidden');
            } else {
                returnTopAction.classList.add('hidden');
            }
            
            if (scrollY > containerUnstickPosition) {
                container.classList.add('-translate-y-full');
            } else {
                container.classList.remove('-translate-y-full');
            }
        };

        const debounce = (func, delay) => {
            let inDebounce;
            return function() {
              const context = this;
              const args = arguments;
              clearTimeout(inDebounce);
              inDebounce = setTimeout(() => func.apply(context, args), delay);
            };
          };
        
          window.addEventListener('scroll', debounce(onScroll, 10));
    }
};

export default HeaderController;